import { animationDuration, Link, wrapperSize } from '@config';
import styled, { css } from 'styled-components';
import { position, rgba, size } from 'polished';
import { Text } from '@components';
import bg from '../../images/bg.png';
import island from '../../images/island.png';
import cat from '../../images/cat.svg';
import arrow from '../../images/arrow.svg';
import light from '../../images/light.png';
import smoke from '../../images/smoke.svg';
import smokeLeft from '../../images/smoke-left.svg';
import smokeRight from '../../images/smoke-right.svg';
import speck from '../../images/speck.svg';
import speckDrop from '../../images/speck-drop.svg';
import {
  backgroundAnimation,
  catGlareAnimation,
  lightAnimation,
  smokeAnimation,
  speckDropAnimation
} from './animations';
import { desktop, laptop } from '@styles';

export interface WrapperProps {
  wrapperScale?: number;
}

export interface Scrolled {
  scrolled: boolean;
}

export interface Position {
  position: number[];
}

export interface SocialLinkProps {
  link: Link;
}

export interface MessageProps extends Position {
  show: boolean;
}

export interface LightProps extends Position {
  delay?: number;
}

export interface SpeckDropProps extends Position {
  length: number;
  duration: number;
  delay?: number;
}

export const Scene = styled.div`
  ${position('fixed', '0', null, null, '0')}
  ${size('100%', '100%')}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

export const Title = styled(Text)<Scrolled>`
  position: absolute;
  left: 50%;
  top: ${({ scrolled }) => scrolled ? 2 : 15}%;
  transform: translateX(-50%);
  font-weight: 700;
  will-change: top;
  transition: top 2s ease-in-out;
  z-index: 30;
`;

export const Wrapper = styled.div<WrapperProps>`
  flex: 1 0 ${wrapperSize.height}px;
  width: ${wrapperSize.width}px;
  height: ${wrapperSize.height}px;
  transform: ${({ wrapperScale }) => wrapperScale && `scale(${wrapperScale})`};
  transform-origin: center;
`;

export const Background = styled.div<Scrolled>`
  ${position('absolute', '0', null, null, '0')}
  ${size('100%', '100%')}
  background: url(${bg}) left top no-repeat;
  background-size: 120% auto;
  transform: ${({ scrolled }) => scrolled && 'scale(3)'};
  transform-origin: center 80%;
  will-change: transform, background-position;
  transition: transform ${animationDuration}ms ease-in-out;
  animation: ${backgroundAnimation};
  z-index: 10;
`;

export const Island = styled.div<Scrolled>`
  ${position('absolute', '35px', null, null, '0')}
  ${size('100%', '100%')}
  background: url(${island}) center top 43% no-repeat;
  background-size: 100% auto;
  transform: ${({ scrolled }) => scrolled && 'scale(2)'};
  transform-origin: center 90%;
  will-change: transform;
  transition: transform ${animationDuration}ms ease-in-out;
  z-index: 20;

  ${laptop(css`
    transform-origin: center 80%;
  `)}
`;

export const Descr = styled.div<Scrolled>`
  ${position('absolute', null, null, null, '50%')}
  bottom: ${({ scrolled }) => scrolled ? 28 : 13}%;
  transform: translateX(-50%);
  text-align: center;
  will-change: bottom;
  transition: bottom 2s ease-in-out;
  z-index: 30;
`;

export const Links = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

export const Contacts = styled.div<Scrolled>`
  ${position('absolute', null, null, null, '50%')}
  bottom: ${({ scrolled }) => scrolled ? 10 : -20}%;
  transform: translateX(-50%);
  will-change: bottom;
  transition: bottom 2s ease-in-out;
  display: flex;
  gap: 32px;
  z-index: 30;
`;

export const SocialLink = styled.a.attrs<SocialLinkProps>(({ link }) => ({
  href: link.href,
  target: link.target
}))<SocialLinkProps>`
  flex: 0 0 80px;
  ${size('80px', '80px')}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => rgba(theme.colors.dark, 0.4)};
  border-radius: 50%;

  ${laptop(css`
    flex: 0 0 106px;
    ${size('106px', '106px')}
  `)}
`;

export const CatGlare = styled.div`
  ${position('absolute', '53.5%', null, null, '46.2%')}
  ${size('260px', '260px')}
  mask: url(${cat});
  mask-repeat: no-repeat;
  mask-size: 110px 130px;
  mask-position: 50px center;
  z-index: 30;

  &::before {
    ${position('absolute', '0', null, null, '0')}
    ${size('100%', '100%')}
    content: '';
    background: conic-gradient(from 180deg at 50% 50%, rgba(217, 217, 217, 0) 0deg, #D9D9D9 360deg);
    border-radius: 50%;
    will-change: transform;
    animation: ${catGlareAnimation};
  }
`;

export const Cat = styled.div`
  ${position('absolute', '57.5%', null, null, '48%')}
  ${size('125px', '103px')}
  cursor: pointer;
  z-index: 35;
`;

export const Message = styled.div<MessageProps>`
  ${({ position: [left, top] }) => (
    position('absolute', `${top}px`, null, null, `${left}px`)
  )}
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  background: ${({ theme }) => theme.colors.light};
  border-radius: 50px;
  font-size: ${({ theme }) => `${theme.fontSize.sm}px`};
  transform: translate(calc(-50% - 5px), calc(-100% - 15px));
  opacity: 0;
  visibility: hidden;
  will-change: opacity, visibility;
  transition: opacity 0.5s ease, visibility 0.5s ease;
  padding: 0 15px;
  z-index: 30;

  ${({ show }) => show && css`
    opacity: 1;
    visibility: visible;
  `}

  ${laptop(css`
    transform: translateY(calc(-100% - 10px));
  `)}

  ${desktop(css`
    height: 72px;
    transform: translateY(calc(-100% - 20px));
    padding: 0 30px;
  `)}
  
  &::before,
  &::after {
    content: '';
    background: ${({ theme }) => theme.colors.light};
    border-radius: 50%;
  }

  &::before {
    ${position('absolute', '27px', null, null, 'calc(50% - 6px)')}
    ${size('13px', '13px')}

    ${laptop(css`
      left: 15px;
    `)}
    
    ${desktop(css`
      top: 54px;
      left: 30px;
      ${size('26px', '26px')}
    `)}
  }

  &::after {
    ${position('absolute', '43px', null, null, 'calc(50% - 10px)')}
    ${size('7px', '7px')}

    ${laptop(css`
      left: 8px;
    `)}

    ${desktop(css`
      top: 86px;
      left: 16px;
      ${size('14px', '14px')}
    `)}
  }
`;

export const Arrow = styled.button<Scrolled>`
  position: absolute;
  bottom: ${({ scrolled }) => scrolled ? -20 : 20}px;
  left: 50%;
  ${size('17px', '20px')}
  transform: translateX(-50%);
  background: url(${arrow}) no-repeat;
  background-size: contain;
  border: 0;
  outline: none;
  cursor: pointer;
  will-change: bottom;
  transition: bottom 0.5s ease-in-out;
  padding: 0;
  z-index: 30;
`;

export const Light = styled.div<LightProps>`
  ${({ position: [left, top] }) => (
    position('absolute', `${top}%`, null, null, `${left}%`)
  )}
  ${size('273px', '273px')}
  background: url(${light}) center center no-repeat;
  background-size: 98px 98px;
  will-change: background-size, opacity;
  animation: ${lightAnimation};
  animation-delay: ${({ delay }) => `${delay}s`};
  z-index: 25;
`;

export const Smoke = styled.div`
  pointer-events: none;
  z-index: 25;

  &::after {
    content: '';
    ${size('412px', '838px')}
    background: url(${smoke}) no-repeat;
    background-size: contain;
    will-change: transform;
    animation: ${smokeAnimation};
  }
`;

export const SmokeLeft = styled(Smoke)`
  ${position('absolute', '85%', null, null, '25%')}
  ${size('222px', '758px')}
  background: url(${smokeLeft}) no-repeat;
  background-size: contain;

  ${laptop(css`
    top: 72%;
    left: 6%;
  `)}

  &::after {
    ${position('absolute', null, null, '-100px', '0')}
  }
`;

export const SmokeRight = styled(Smoke)`
  ${position('absolute', '85%', '20%', null, null)}
  ${size('222px', '758px')}
  background: url(${smokeRight}) no-repeat;
  background-size: contain;

  ${laptop(css`
    ${position('absolute', '78%', '0', null, null)}
  `)}

  &::after {
    ${position('absolute', '0', null, null, '0')}
  }
`;

export const Speck = styled.div`
  position: absolute;
  ${position('absolute', '10%', null, null, '2%')}
  ${size('846px', '2776px')}
  background: url(${speck}) no-repeat;
  background-size: contain;
  pointer-events: none;
  z-index: 15;
`;

export const SpeckDrop = styled.div<SpeckDropProps>`
  ${({ position: [left, top] }) => (
    position('absolute', `${top}%`, null, null, `${left}%`)
  )}
  ${({ length }) => size(`${length}%`, '106px')}
  background: url(${speckDrop}) center top no-repeat;
  background-size: contain;
  will-change: opacity, background-position, background-size;
  animation: ${speckDropAnimation};
  animation-duration: ${({ duration }) => `${duration}s`};
  animation-delay: ${({ delay = 0 }) => `${delay}s`};
  pointer-events: none;
  z-index: 25;
`;